/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { type ComponentType, Suspense, lazy, useMemo } from 'react';

import type { Sources, SourcesByResolution } from '@coursera-web/video-player';

import { Skeleton } from '@coursera/cds-core';

import { createContainer } from 'bundles/naptimejs';
/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import AssetsV1 from 'bundles/naptimejs/resources/assets.v1';

const FALLBACK_RESOLUTION = '360p';
const VIDEO_MP4 = 'video/mp4';

// VideoPlayer pulls in the entire video.js runtime (~140kB before gzip).
// SimpleVideoPlayer is mounted from a dialog that the user opens on demand,
// so defer the import until the component actually renders.
const VideoPlayer = lazy(() => import('./VideoPlayer'));

const styles = {
  container: css`
    aspect-ratio: 16 / 9;
  `,
};

type VideoSourceUrlsByResolution = Record<string, Record<string, { url: string }>>;

type Asset = {
  videoSourceUrls?: VideoSourceUrlsByResolution;
  url?: { url?: string };
};

type Props = {
  assetId?: string;
  src?: string;
  assets?: Asset;
};

const buildSources = (assets: Asset | undefined, src: string | undefined): SourcesByResolution | null => {
  if (assets?.videoSourceUrls) {
    return Object.fromEntries(
      Object.entries(assets.videoSourceUrls).map(([resolution, byType]) => [
        resolution,
        Object.fromEntries(Object.entries(byType).map(([type, { url }]) => [type, url])) as Sources,
      ])
    );
  }
  const url = assets?.url?.url || src;
  return url ? { [FALLBACK_RESOLUTION]: { [VIDEO_MP4]: url } } : null;
};

/**
 * A simple video player that can either play `src` or videos from the asset
 * service. One of `src` or `assetId` must be present; if both are present,
 * `assetId` wins. A bare `src` is assumed to be `video/mp4`.
 */
export const SimpleVideoPlayer = ({ src, assets }: Props) => {
  const sources = useMemo(() => buildSources(assets, src), [assets, src]);

  if (!sources) {
    return null;
  }

  return (
    <div css={styles.container}>
      <Suspense fallback={<Skeleton variant="rect" height="100%" width="100%" />}>
        <VideoPlayer sources={sources} layoutMode="fill" sentryErrorSource="SimpleVideoPlayer" />
      </Suspense>
    </div>
  );
};

export default createContainer(SimpleVideoPlayer, ({ assetId }: { assetId?: string }) => ({
  assets:
    assetId &&
    AssetsV1.get(assetId, {
      fields: ['videoSourceUrls'],
    }),
})) as ComponentType<Props>;
